export const MONTH_MAPPING_KEY = "month_mapping_by_fiscal_year";
export const FORECAST_MONTH_MAPPING_KEY = "forecast_months_by_fiscal_year";

/**
 * Class responsible for transforming financial data into row format
 */
class RowTransformer {
  /**
   * Constructor initializes the rows collection
   */
  constructor() {
    this.rows = [];
  }

  /**
   * Transforms financial data into row format
   * @param {Array} data - The array of financial data objects
   * @returns {Array} An array of formatted rows
   */
  transform(data) {
    this.rows = [];

    // Process each record in the data array
    data.forEach((record) => {
      const { fiscal_year, financial_data, fiscal_year_type } = record;
      // Create a unique key that includes year, type, and version
      const fiscalYearKey = `${fiscal_year}|${fiscal_year_type}`;

      // Process each financial data item (indicator)
      financial_data.forEach((item) => {
        this.processIndicatorData(
          record.activity_name,
          record.site_name,
          item,
          fiscalYearKey,
        );
      });
    });

    return this.rows;
  }

  /**
   * Process a single indicator's data and update or create rows accordingly
   * @param {string} activity_name - The name of the activity
   * @param {string} site_name - The name of the site
   * @param {Object} indicator - The current indicator data
   * @param {string} fiscalYearKey - The unique fiscal year key (includes type and version)
   */
  processIndicatorData(activity_name, site_name, indicator, fiscalYearKey) {
    const { indicator_name, indicator_order, inputs } = indicator;

    // Determine if this is forecast data based on fiscal year type (F = Forecast)
    const isForecast = fiscalYearKey.includes("|F");

    // Create an input-like object for compatibility with existing code
    const inputData = {
      name: indicator_name,
      activity: activity_name,
      site: site_name,
      order: indicator_order,
      line_above: indicator.indicator_blank_line_above,
      color_text: indicator.indicator_color_text,
      color_background: indicator.indicator_color_background,
      display_children: false,
      fiscal_months: inputs.map((input) => ({
        month: input.month,
        amount: input.amount,
        is_forecast: isForecast,
      })),
    };

    const existingRow = this.findRowByName(
      indicator_name,
      site_name,
      activity_name,
    );

    if (existingRow) {
      this.updateExistingRow(existingRow, inputData, fiscalYearKey);

      // Check if this row needs a line above but doesn't have a separator
      if (
        inputData.line_above &&
        !this.hasSeparatorForRow(indicator_name, site_name)
      ) {
        this.addSeparatorRow(inputData);
      }
    } else {
      this.createNewRow(inputData, fiscalYearKey);
    }
  }

  /**
   * Checks if a separator exists for the given row
   * @param {string} name - The indicator name
   * @param {string} site - The site name
   * @param {string} activity - The activity name
   * @returns {boolean} True if a separator exists
   */
  hasSeparatorForRow(name, site, activity) {
    return this.rows.some(
      (row) =>
        row.is_separator_for === name &&
        row.site === site &&
        row.activity === activity,
    );
  }

  /**
   * Find a row by name, site and activity
   * @param {string} name - The name to search for
   * @param {string} site - The site to search for
   * @param {string} activity - The activity to search for
   * @returns {Object|undefined} The found row or undefined
   */
  findRowByName(name, site, activity) {
    return this.rows.find(
      (row) =>
        row.name === name && row.site === site && row.activity === activity,
    );
  }
  /**
   * Updates an existing row with new fiscal data
   * @param {Object} row - The existing row to update
   * @param {Object} input - The input data
   * @param {string} fiscalYearKey - The unique fiscal year key
   */
  updateExistingRow(row, input, fiscalYearKey) {
    // Initialize fiscal year object if it doesn't exist
    if (!row[MONTH_MAPPING_KEY][fiscalYearKey]) {
      row[MONTH_MAPPING_KEY][fiscalYearKey] = {};
    }

    // Initialize forecast months object if it doesn't exist
    if (!row[FORECAST_MONTH_MAPPING_KEY][fiscalYearKey]) {
      row[FORECAST_MONTH_MAPPING_KEY][fiscalYearKey] = {};
    }

    this.addMonthData(
      row[MONTH_MAPPING_KEY][fiscalYearKey],
      row[FORECAST_MONTH_MAPPING_KEY][fiscalYearKey],
      input.fiscal_months,
    );

    // Update properties if they exist in the input
    if (input.order !== undefined) row.order = input.order;
  }

  /**
   * Adds an empty separator row
   * @param {Object} input - The input data
   */
  addSeparatorRow(input) {
    const separatorRow = {
      name: "",
      activity: input.activity,
      site: input.site,
      order: input.order - 0.5, // Place just before the input row
      is_separator_for: input.name,
      is_separator: true, // Flag clair
      [MONTH_MAPPING_KEY]: null, // Pas de données
      [FORECAST_MONTH_MAPPING_KEY]: null,
    };

    this.rows.push(separatorRow);
  }

  /**
   * Creates a new row and adds it to the rows collection
   * @param {Object} input - The input data
   * @param {string} fiscalYearKey - The unique fiscal year key
   */
  createNewRow(input, fiscalYearKey) {
    // If line_above is true, add an empty separator row
    if (input.line_above) {
      this.addSeparatorRow(input);
    }
    
    // Initialize the row with basic information
    const newRow = {
      name: input.name,
      activity: input.activity,
      site: input.site,
      order: input.order,
      is_separator: false,
      line_above: input.line_above,
      color_text: input.color_text,
      color_background: input.color_background,
      [MONTH_MAPPING_KEY]: {
        [fiscalYearKey]: {},
      },
      [FORECAST_MONTH_MAPPING_KEY]: {
        [fiscalYearKey]: {},
      },
    };

    // Add month data
    this.addMonthData(
      newRow[MONTH_MAPPING_KEY][fiscalYearKey],
      newRow[FORECAST_MONTH_MAPPING_KEY][fiscalYearKey],
      input.fiscal_months,
    );

    this.rows.push(newRow);
  }

  /**
   * Adds month data to target objects
   * @param {Object} target - The target object to add month data to
   * @param {Object} forecastTarget - The target object to add forecast month data to
   * @param {Array} fiscalMonths - Array of month data
   */
  addMonthData(target, forecastTarget, fiscalMonths) {
    if (!fiscalMonths) return;

    fiscalMonths.forEach((monthData) => {
      const { month, amount, is_forecast } = monthData;

      // Always add the amount to the regular month mapping
      target[month] = amount;

      // If this is a forecast month, also add it to the forecast mapping
      if (is_forecast) {
        forecastTarget[month] = true;
      }
    });
  }

  /**
   * Reorganizes rows by order property, keeping separators before their rows
   */
  reorganizeRows() {
    // Create a map of separator rows by the rows they're separating
    const separatorMap = new Map();
    const regularRows = [];

    // Separate out the separator rows
    this.rows.forEach((row) => {
      if (row.is_separator_for) {
        // Create a unique key that includes name, site AND activity
        const separatorKey = `${row.is_separator_for}|${row.site}|${row.activity}`;
        separatorMap.set(separatorKey, row);
      } else {
        regularRows.push(row);
      }
    });

    // Sort regular rows by order
    regularRows.sort((a, b) => (a.order || 0) - (b.order || 0));

    // Build the final sorted rows array with separators properly placed
    this.rows = [];

    regularRows.forEach((row) => {
      // Add separator first if this row has one
      const separatorKey = `${row.name}|${row.site}|${row.activity}`;
      if (separatorMap.has(separatorKey)) {
        this.rows.push(separatorMap.get(separatorKey));
      }
      // Add the row itself
      this.rows.push(row);
    });
  }
}

/**
 * Transforms financial data into row format
 * @param {Array} data - The array of financial data objects
 * @returns {Array} An array of formatted rows
 */
export const formDataToRow = (data) => {
  const transformer = new RowTransformer();
  transformer.transform(data);
  transformer.reorganizeRows();

  return transformer.rows;
};
